/**
 * Returns the slice of a scope config to use for a widget instance.
 * Lookup priority (highest first):
 *   1. compositeKey (e.g. "urn:widget:ticker#team-b") — instance-specific slice
 *   2. widgetUrn (e.g. "urn:widget:ticker") — widget-level default
 *   3. Flat object — backward compat when config is not keyed by URN
 */
function scopeSliceForWidget(
  scopeConfig: Record<string, unknown> | null | undefined,
  widgetUrn: string,
  compositeKey?: string
): Record<string, unknown> {
  if (!scopeConfig || typeof scopeConfig !== 'object') { return {} }
  // 1. Instance-specific slice (composite key wins when present)
  if (compositeKey) {
    const instanceSlice = scopeConfig[compositeKey]
    if (instanceSlice != null && typeof instanceSlice === 'object' && !Array.isArray(instanceSlice)) {
      return instanceSlice as Record<string, unknown>
    }
  }
  // 2. Widget-level slice (URN only)
  const slice = scopeConfig[widgetUrn]
  if (slice != null && typeof slice === 'object' && !Array.isArray(slice)) {
    return slice as Record<string, unknown>
  }
  // 3. Flat fallback (backward compat)
  return scopeConfig as Record<string, unknown>
}

/**
 * Widget config merge. Resolution order (lowest to highest precedence): default → frame → playlist → campaign.
 * Campaign wins. Only defined, non-null values from higher tiers overwrite lower tiers.
 * When widgetUrn is provided, campaign and playlist configs are resolved per-widget: keyed shape
 * (e.g. { "lucimark-adsb-exchange": { "zoom": "12" } }) uses that widget's slice; flat shape applies to all widgets.
 * When instanceId is also provided, a composite key `${widgetUrn}#${instanceId}` is checked first in each
 * scope, enabling per-instance config overrides at the playlist/campaign tiers.
 */
export function mergeWidgetConfig(params: {
  /** Tier 1: asset / widget defaults (fixedDefaultConfig, package.json defaults). */
  assetConfig?: Record<string, unknown> | null
  /** Tier 4: campaign-level config (highest precedence). */
  campaignConfig?: Record<string, unknown> | null
  /** Tier 3: playlist-level config. */
  playlistConfig?: Record<string, unknown> | null
  /** Tier 2: frame / facet config (per-section widget config). */
  facetConfig?: Record<string, unknown> | null
  /** Widget URN (facet.asset) so campaign/playlist config can be resolved per-widget when stored keyed. */
  widgetUrn?: string
  /**
   * Optional singleton instance ID. When provided together with widgetUrn, a composite key
   *  `${widgetUrn}#${instanceId}` is tried first in each scope before falling back to the URN-level slice.
   */
  instanceId?: string
}): Record<string, unknown> {
  const { assetConfig, campaignConfig, playlistConfig, facetConfig, widgetUrn, instanceId } = params
  const compositeKey = widgetUrn && instanceId ? `${widgetUrn}#${instanceId}` : undefined
  const campaign = widgetUrn
    ? scopeSliceForWidget(campaignConfig ?? null, widgetUrn, compositeKey)
    : (campaignConfig ?? {})
  const playlist = widgetUrn
    ? scopeSliceForWidget(playlistConfig ?? null, widgetUrn, compositeKey)
    : (playlistConfig ?? {})
  // Only overwrite with meaningful values so asset (fixedDefaultConfig) isn't clobbered by undefined, null, or ''.
  const definedOnly = (o: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''))
  // Resolution order: default (1) → frame (2) → playlist (3) → campaign (4). Campaign wins.
  return {
    ...(assetConfig ?? {}),
    ...definedOnly(facetConfig ?? {}),
    ...definedOnly(playlist),
    ...definedOnly(campaign)
  }
}
